/**
 * This module is responsible for deriving the RouterConfig. Freestanding apps can pass their
 * RouterConfig into FSBL.startApp(). When not passed in, the RouterConfig should default to the IAC
 * for freestanding apps.
 *
 * When runnning in FEA, the router config is obtained from the system manifest.
 */
import { RouterConfig, RouterTransportSettings } from "../../clients/routerClient";
import { Logger } from "../logger";
import { System } from "../../common/system";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { SystemStartupData } from "./types";
import { Manifest } from "../../types";

const FreestandingRouterConfig = {
	sameDomainTransport: "FinsembleTransport",
	crossDomainTransport: "FinsembleTransport",
	transportSettings: {
		FinsembleTransport: {
			serverAddress: "ws://127.0.0.1:3376",
		},
	},
	transportName: "FinsembleTransport",
};

/**
 * Determines if the current window is in the same domain as the provided documentRoot.
 * The documentRoot is typically from the "finsemble.moduleRoot" entry of the system manifest
 * which is expanded into an absolute domain by FEA (see Manifest.ts in electron-adapter package).
 */
const isCrossDomain = (documentRoot?: string) => {
	if (!documentRoot) {
		Logger.system.error(
			"No documentRoot provided to RouterConfig.isCrossDomain(). Check the moduleRoot entry of the manifest."
		);
		return true;
	}
	const parser = document.createElement("a");
	parser.href = documentRoot;

	const isSameHost = window.location.hostname === parser.hostname;

	const isSameProtocol = window.location.protocol === parser.protocol;

	const wport = window.location.port !== undefined ? window.location.port : 80;
	const pport = parser.port !== undefined ? parser.port : 80;
	const isSamePort = wport === pport;

	const sameDomain = isSameHost && isSamePort && isSameProtocol;
	Logger.system.debug(`Transport crossDomain=${isCrossDomain} (${isSameHost}:${isSameProtocol}:${isSamePort})`);
	return !sameDomain;
};

/**
 * Returns the recommended transport given the configurations in the system manifest. The transport depends
 * on whether this window is running in the same domain as the router service (by checking the finsemble.moduleRoot entry in
 * the system manifest).
 */
export const getRecommendedTransport = (systemManifest: any) => {
	const { moduleRoot, router } = systemManifest?.finsemble;
	const { sameDomainTransport = "SharedWorker", crossDomainTransport = "IPCBus" } = router ?? {};

	return isCrossDomain(moduleRoot) ? crossDomainTransport : sameDomainTransport;
};

/**
 * Converts a manifest into a RouterConfig object which can be used to start the router.
 */
const routerConfigFromManifest = (systemManifest: any, windowName: string): RouterConfig => {
	Logger.system.info("WINDOW LIFECYCLE:STARTUP:RouterClient:routerConfigFromManifest");
	// If manifest is a string, then there was an error getting the manifest because in a separate application
	if (!systemManifest || typeof systemManifest === "string") {
		Logger.system.error(
			"WINDOW LIFECYCLE:STARTUP:RouterClient:routerConfigFromManifest failed -- fatal error. No manifest found.",
			systemManifest
		);
		return {
			...FreestandingRouterConfig,
			windowName,
		};
	} else {
		const finConfig = systemManifest.finsemble;

		// Config for transportSettings is preferred. finsemble.IAC is deprecated but might still be floating around in production.
		const transportSettings = finConfig?.router?.transportSettings ?? finConfig?.IAC ?? {};

		const routerConfig: RouterConfig = {
			windowName,
			routerDomainRoot: finConfig.moduleRoot,
			transportName: getRecommendedTransport(systemManifest),
			transportSettings,
		};

		return routerConfig;
	}
};

type GetRouterConfigParams = {
	windowName: string;
	systemManifest?: any;
	transportSettings?: RouterTransportSettings;
};

/**
 * Retrieves a router configuration given a windowName, optional transportSettings and optional systemManifest.
 * If a systemManifest is provided (e.g. FSBLDesktop) then the router config from the manifest will be used
 * otherwise it will default to using IAC (FinsembleTransport). The default IAC port of 127.0.0.1:3376 will
 * be used unless transportSettings is passed in to override it.
 */
export const getRouterConfig = ({
	windowName,
	systemManifest,
	transportSettings,
}: GetRouterConfigParams): RouterConfig => {
	if (systemManifest) {
		// FEA
		return routerConfigFromManifest(systemManifest, windowName);
	} else {
		// Freestanding
		return {
			...FreestandingRouterConfig,
			windowName,
			transportSettings: transportSettings ?? {},
		};
	}
};

/** Converts System.Window.getCurrent().getOptions() into a simple async call */
export const getSystemStartupData = async () =>
	new Promise<SystemStartupData>((resolve) => {
		System.Window.getCurrent().getOptions((options) => {
			resolve(options);
		});
	});

/**
 * Returns a system manifest that has been run through the config resolver to replace
 * variables with absolute paths (e.g. $applicationRoot, $moduleRoot)
 */
export const getManifestFromStartupData = (systemStartupData: SystemStartupData): Manifest => {
	/**
	 * systemStartupData.manifest contains the startup manifest, before the imports are processed.
	 * At early boot stages this is all that is available. Once the entire manifest has been processed
	 * for imports it becomes available in systtemStartupData.customData.manifest. For the purposes
	 * of system startup it really doesn't matter which we use because all the important entries are
	 * in the system manifest.
	 */
	let systemManifest = systemStartupData?.manifest;

	if (systemManifest?.finsemble) ConfigUtil.resolveManifestMacros(systemManifest, systemManifest.finsemble);
	return systemManifest;
};
